import get from 'lodash/get';
import inRange from 'lodash/inRange';
import debounce from 'lodash/debounce';
import request from '@flowio/request';
import session from '@flowio/session';
import { postRedirect } from '@flowio/browser-helpers';

import { getFeatures, isFeatureInResultEnabled } from './features';
import { getCart } from './cart_unified/cart-unified';
import { getOrderUrl } from './orders';
import config from './config';
import { getShopifyCart } from './cart_unified/utilities';
import globalFeatures from './constants/features';
import events from './events';
import { locationRedirect } from './utilities/location-redirect';
import log from './utilities/log';
import showErrorModal from './utilities/error-modal';
import addLanguageParam from './utilities/add-language-param';
import { getCustomerTokenId, getCustomerToken } from './utilities/customer-tokens';
import { setPromotionCode, openCheckout, isEnabled as isBlazeEnabled } from './blaze';

const prefix = config.SHOPIFY_CHECKOUT_PATH_PREFIX;
const organizationId = config.FLOW_ORGANIZATION;
const flowApiBaseURI = config.API_BASE_URI;
const EventConstants = events.constants;
const CheckoutRedirectTypes = {
  Organization: 'organization',
  CheckoutResource: 'checkout_resource',
};

/** @returns  {Promise<import('@flowio/types/dist/api').ShopifyCartConversion>} */
function getFlowOrderFromCart(cart) {
  return new Promise((resolve, reject) => {
    getCart().shopifyCart.getFlowOrderFromCart(cart, {
      cache: 'disabled',
      onSuccess: order => resolve(order),
      onError: error => reject(error),
    });
  });
}

function initializeCheckout(checkoutForm) {
  return addLanguageParam(`${prefix}/initialize`).then((urlWithLanguageParam) => {
    postRedirect(urlWithLanguageParam, checkoutForm);
  });
}

export function initializeCheckoutWithOrderForm(orderForm) {
  const customerTokenElement = window.document.querySelector('[flow-customer-token]');

  return initializeCheckout({
    discriminator: 'checkout_order_form',
    customerTokenId: getCustomerTokenId(),
    customerToken: getCustomerToken(customerTokenElement ? customerTokenElement.value : undefined),
    organizationId,
    orderForm,
    sessionId: session.getSessionId(),
  });
}

export function initializeCheckoutWithOrderNumber(orderNumber) {
  // Adding check for empty orders as we have seen errors in prod
  // of orders getting through to checkout with `emp-` prefix.
  if (/^emp-/.test(orderNumber)) {
    showErrorModal(new Error('Error: Cart is empty.'));
    return Promise.resolve();
  }

  const customerTokenElement = window.document.querySelector('[flow-customer-token]');

  return initializeCheckout({
    discriminator: 'checkout_reference_form',
    customerTokenId: getCustomerTokenId(),
    customerToken: getCustomerToken(customerTokenElement ? customerTokenElement.value : undefined),
    organizationId,
    orderNumber,
    sessionId: session.getSessionId(),
  });
}

export function initializeCheckoutWithShopifyCart(shopifyCartId) {
  const customerTokenElement = window.document.querySelector('[flow-customer-token]');

  return initializeCheckout({
    discriminator: 'shopify_cart_reference_form',
    cartId: shopifyCartId,
    customerTokenId: getCustomerTokenId(),
    customerToken: getCustomerToken(customerTokenElement ? customerTokenElement.value : undefined),
    organizationId,
    sessionId: session.getSessionId(),
  });
}

export function initializeCheckoutWithCart(existingCart) {
  getShopifyCart(existingCart)
    .then(shopifyCart => getFlowOrderFromCart(shopifyCart))
    .then((localizedData) => {
      const orderNumber = get(localizedData, 'flow.order.number');
      const errors = localizedData.flow.errors || [];

      if (errors.length > 0) {
        showErrorModal(new Error(`Error: ${errors.map(err => err.messages.join(', ')).join('; ')}`));
        return;
      }

      // Adding check for empty orders as we have seen errors in prod
      // of orders getting through to checkout with `emp-` prefix.
      if (/^emp-/.test(orderNumber)) {
        showErrorModal(new Error('Error: Cart is empty.'));
        return;
      }
      initializeCheckoutWithOrderNumber(orderNumber);
    });
}

export function setBlazePromotionCode(promotionCode) {
  if (isBlazeEnabled()) {
    setPromotionCode(promotionCode);
  } else {
    log(`Error: Promotion Code "${promotionCode}" will not be applied because Blaze is not enabled`);
  }
}

function redirectCheckouts(cart) {
  const organization = session.getOrganization();
  const experience = session.getExperience();
  const url = `${flowApiBaseURI}/${organization}/experiences/${experience}/shopify/cart/checkouts`;

  request.authenticated(
    {
      url,
      method: 'POST',
      data: cart,
    },
    (status, response) => {
      if (inRange(status, 200, 300)) {
        const errors = response && response.builder ? response.builder.errors : undefined;
        if (errors && errors.length > 0) {
          showErrorModal(response.builder);
          log(`Error creating checkout from cart: \n\n Response: ${response}`);
          return;
        }
        const checkoutUrl = getOrderUrl({ checkoutId: response.id });
        addLanguageParam(checkoutUrl).then((urlWithLanguageParam) => {
          locationRedirect(urlWithLanguageParam);

          events.trigger(EventConstants.CHECKOUT_REDIRECT, {
            type: CheckoutRedirectTypes.CheckoutResource,
            location: urlWithLanguageParam,
          });
        });
      } else {
        showErrorModal(response);
        log(`Error creating checkout from cart: \n\n Status ${status}\n Response: ${response}`);
      }
    },
  );
}

const redirect = debounce((existingCart) => {
  Promise.all([
    getFeatures(),
    getShopifyCart(existingCart),
    isBlazeEnabled(),
  ]).then(([featureValuesResult, shopifyCart, blazeEnabled]) => {
    const isReturningCustomersEnabled = isFeatureInResultEnabled(
      globalFeatures.RETURNING_CUSTOMERS,
      featureValuesResult,
    );

    const isCheckoutResourceEnabled = isFeatureInResultEnabled(
      globalFeatures.CHECKOUT_RESOURCE,
      featureValuesResult,
    );


    const isCheckoutInitializeEnabled = isFeatureInResultEnabled(
      globalFeatures.CHECKOUT_INITIALIZE_ENDPOINT,
      featureValuesResult,
    );

    if (blazeEnabled) {
      openCheckout(shopifyCart.items.map(item => ({
        number: item.variant_id,
        quantity: item.quantity,
        attributes: item.properties,
      })));
    } else if (isReturningCustomersEnabled || isCheckoutInitializeEnabled) {
      initializeCheckoutWithCart(shopifyCart);
    } else if (isCheckoutResourceEnabled) {
      redirectCheckouts(shopifyCart);
    } else {
      getFlowOrderFromCart(shopifyCart).then((localizedData) => {
        const orderNumber = get(localizedData, 'flow.order.number');
        const path = `${prefix}/${organizationId}/checkout/${orderNumber}`;
        locationRedirect(path);
        events.trigger(EventConstants.CHECKOUT_REDIRECT, {
          type: CheckoutRedirectTypes.Organization,
        });
      });
    }
  });
}, 500, { leading: true });

module.exports = {
  redirect,
  CheckoutRedirectTypes,
  initializeCheckoutWithOrderForm,
  initializeCheckoutWithShopifyCart,
  initializeCheckoutWithOrderNumber,
  initializeCheckoutWithCart,
  setBlazePromotionCode,
};



// WEBPACK FOOTER //
// ./static/js/v0/checkout.js